import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/lib/lenis";
import { LogoMark } from "./LogoMark";

const LINES = ["Poured from", "the pastoral", "heart of Gujarat."];

const STATS = [
  { value: "100+", label: "Cattle in our own shed" },
  { value: "4", label: "Organic milk products" },
  { value: "2", label: "Locations · Gift City & Vyara" },
  { value: "Sat–Sun", label: "Farm visits · advance booking" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-32 lg:pt-40 pb-20 lg:pb-28">
      <div
        aria-hidden="true"
        className="pointer-events-none select-none absolute -bottom-6 left-1/2 -translate-x-1/2 w-full text-center font-serif font-semibold text-[19vw] leading-none whitespace-nowrap text-outline-pine"
      >
        ADVESHTA
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="flex flex-wrap items-center gap-3 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-brand"
          >
            <span>Adveshta Projects Private Limited</span>
            <span className="h-1.5 w-1.5 rotate-45 bg-gold inline-block" />
            <span data-testid="hero-since">Working since 2022</span>
            <span className="h-1.5 w-1.5 rotate-45 bg-gold inline-block" />
            <span>Gift City · Gandhinagar</span>
          </motion.div>

          <h1
            data-testid="hero-heading"
            className="mt-8 font-serif font-semibold text-[clamp(2.9rem,7.2vw,6.4rem)] leading-[0.98] tracking-tight text-pine"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.2 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 1 ? (
                    <>
                      the <em className="italic text-gold">pastoral</em>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-5 font-gujarati text-lg md:text-xl text-gold"
          >
            શુદ્ધ. તાજું. ગામડાંનું.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-sage"
          >
            Adveshta Milk brings organic cow milk, curd, ghee and buttermilk from Vyara to your
            table — sourced direct from village farmers through fair trade and processed the
            organic way, with nothing synthetic added.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-products"
              onClick={() => scrollToSection("products")}
              className="group inline-flex items-center gap-3 rounded-full bg-pine text-cream px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-royal transition-colors duration-300"
            >
              Explore our products
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-story"
              onClick={() => scrollToSection("story")}
              className="group inline-flex items-center gap-2 rounded-full border border-pine/30 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-pine hover:bg-pine hover:text-cream transition-colors duration-300"
            >
              Our story
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.1 }}
            className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-y-8 border-t border-sand pt-7"
          >
            {STATS.map((s) => (
              <div key={s.label} className="pr-4">
                <p className="font-serif text-3xl md:text-4xl xl:text-5xl text-pine whitespace-nowrap">{s.value}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-sage">
                  {s.label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5 mt-4 lg:mt-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div
              aria-hidden="true"
              className="absolute inset-0 translate-x-4 translate-y-4 rounded-t-full border border-gold/60"
            />
            <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-t-full">
              <img
                data-testid="hero-image"
                src="https://images.unsplash.com/photo-1662406487015-3ac07fabe417?auto=format&fit=crop&w=1200&q=80"
                alt="Cows grazing on open pasture"
                className="h-[420px] md:h-[560px] lg:h-[640px] w-full object-cover"
              />
              <div className="absolute inset-0 bg-pine/10 mix-blend-multiply" />
            </motion.div>

            <div className="absolute -left-8 md:-left-12 bottom-10 hidden sm:grid place-items-center">
              <svg viewBox="0 0 120 120" className="h-28 w-28 md:h-32 md:w-32 animate-spin-slow">
                <defs>
                  <path
                    id="badge-circle"
                    d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0"
                    fill="none"
                  />
                </defs>
                <text
                  className="fill-pine"
                  style={{ fontSize: "10px", letterSpacing: "2.6px", fontFamily: "'Source Code Pro', monospace" }}
                >
                  <textPath href="#badge-circle">ORGANIC · FAIR TRADE · VILLAGE FIRST ·</textPath>
                </text>
              </svg>
              <LogoMark className="absolute h-10 w-10" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
